import React, {useEffect, useMemo, useRef, useState} from "react";
import InputField from "../../InputField";
import ModalWidget from "../ModalWidget";
import {ReactComponent as ClassSvg} from "../../../assets/svg/class-border.svg";
import {ReactComponent as ClassSolidSvg} from "../../../assets/svg/class-solid.svg";
import {createGradeScheme} from "../../../services/GradeSchemeService";
import {createRubric, updateRubric} from "../../../services/RubricService";
import {useParams} from "react-router-dom";
import {useMenu} from "../../../contexts/MenuContext";
import {getClassroomById} from "../../../services/ClassroomService";
import FormError from "../../FormError";
import UpgradeErrorMessage from "../../UpgradeErrorMessage";
import {isUpgradeError} from "../../../utils/upgradeError";
import {useClassroom} from "../../../contexts/ClassroomContext";
import {useUser} from "../../../contexts/UserContext";
import {useToast} from "../../../contexts/ToastContext";
import {useClassroomMutations} from "../../../hooks/mutations/useClassroomMutations";
import GradingDefaultsPicker from "../../grading/GradingDefaultsPicker";
import { gradingSummaryLabel } from "../../../utils/gradingDefaults";

interface ClassroomModalProps {
    show?: boolean;
    onHide?: () => void;
    onAddedClassroom?: () => void;
}

function ClassroomModal({show = true, onHide = ()=>{}, onAddedClassroom}: ClassroomModalProps) {
    const {navigateBack} = useMenu()
    const {id} = useParams()
    const {user} = useUser()
    const {showSuccess, showError} = useToast()
    const {addClassroomMutation, editClassroomMutation} = useClassroomMutations()

    const [selectedGradingScheme, setSelectedGradingScheme] = useState<any>(null)
    const [selectedRubric, setSelectedRubric] = useState<any>(null)

    const [classroomName, setClassroomName] = useState<string>('')
    const [error, setError] = useState<Record<string, string> | string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const {addNewClassroom, updateClassroom, setPendingClassroomSelection} = useClassroom()
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'completed'>('idle');
    const completedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const initialNameRef = useRef<string>('');
    const initialSchemeIdRef = useRef<string | null>(null);
    const initialRubricIdRef = useRef<string | null>(null);
    const [initialSchemeId, setInitialSchemeId] = useState<string | null>(null);
    const [initialRubricId, setInitialRubricId] = useState<string | null>(null);
    const [initialSchemeTemplateKey, setInitialSchemeTemplateKey] = useState<string | null>(null);
    const [initialRubricTemplateKey, setInitialRubricTemplateKey] = useState<string | null>(null);
    const [contentDirty, setContentDirty] = useState(false);

    const schoolSettings = user?.school?.settings;
    const schoolDefaultSchemeId = schoolSettings?.default_grade_scheme_id ?? null;
    const schoolDefaultRubricId = schoolSettings?.default_rubric_id ?? null;

    const hasChanges =
        contentDirty ||
        classroomName !== initialNameRef.current ||
        (selectedGradingScheme?.uuid ?? selectedGradingScheme?.id ?? selectedGradingScheme?.key ?? null) !== initialSchemeIdRef.current ||
        (selectedRubric?.id ?? selectedRubric?.key ?? null) !== initialRubricIdRef.current;

    const collapsedSummary = useMemo(
        () => gradingSummaryLabel(selectedRubric, selectedGradingScheme),
        [selectedRubric, selectedGradingScheme],
    );

    const userRole = user?.type;
    const canEditGradingScheme = userRole === 'admin' || schoolSettings?.teacher_subject_access === 'all';
    const canEditRubric = userRole === 'admin' || schoolSettings?.teacher_subject_access === 'all';

    useEffect(() => {
        if (id) {
            getClassroomData(id);
        }
        return () => {
            if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const goBack = () => {
        navigateBack();
        onHide?.();
    };

    const handleGradingSchemeAdded = async (scheme: any) => {
        const schemeId = scheme?.id ?? scheme?.uuid ?? null;
        setSelectedGradingScheme(scheme);
        setContentDirty(true);
        if (id && schemeId) {
            try {
                const updated = await editClassroomMutation.mutateAsync({
                    id,
                    payload: { default_grade_scheme_id: schemeId },
                });
                updateClassroom(updated);
            } catch {
                /* best-effort */
            }
        }
    };

    const handleRubricAdded = async (rubric: any) => {
        const rubricId = rubric?.id ?? null;
        setSelectedRubric(rubric);
        setContentDirty(true);
        if (id && rubricId) {
            try {
                const updated = await editClassroomMutation.mutateAsync({
                    id,
                    payload: { rubric_id: rubricId },
                });
                updateClassroom(updated);
            } catch {
                /* best-effort */
            }
        }
    };

    const getClassroomData = async (classroomId: string) => {
        setLoading(true)
        try {
            const data = await getClassroomById(classroomId);
            setClassroomName(data.name);
            initialNameRef.current = data.name;
            if (data.grade_scheme) {
                setSelectedGradingScheme(data.grade_scheme);
                initialSchemeIdRef.current = data.grade_scheme.id ?? null;
                setInitialSchemeId(data.grade_scheme.id ?? null);
                setInitialSchemeTemplateKey((data.grade_scheme as any).key ?? null);
            }
            if (data.rubric) {
                setSelectedRubric(data.rubric);
                initialRubricIdRef.current = data.rubric.id ?? null;
                setInitialRubricId(data.rubric.id ?? null);
                setInitialRubricTemplateKey((data.rubric as any).key ?? null);
            }
        } catch {}
        finally {
            setLoading(false)
        }
    };

    const addClassroom = async ()=>{
        try {
            if (!classroomName.trim()) {
                setError({ name: 'Class name is required' } as any);
                return;
            }
            const schoolId = user?.school_id || user?.school?.id;
            if (!schoolId) {
                setError('Unable to determine school. Please try again.');
                return;
            }
            setSaveState('saving');
            setError('');

            let classroomData: any;
            if (!id) {
                classroomData = await addClassroomMutation.mutateAsync({ name: classroomName, school_id: schoolId });
            }

            let gradeSchemeId: string | null = selectedGradingScheme?.id || null;
            if (selectedGradingScheme && !gradeSchemeId && selectedGradingScheme.scheme) {
                const created = await createGradeScheme(
                    schoolId, selectedGradingScheme.name,
                    selectedGradingScheme.scheme, selectedGradingScheme.description
                );
                gradeSchemeId = created.data.id;
            }
            if (!gradeSchemeId && schoolDefaultSchemeId) {
                gradeSchemeId = schoolDefaultSchemeId;
            }

            let rubricIdToLink: string | null = null;
            const effectiveClassroomId = id ?? classroomData?.id;

            if (selectedRubric && effectiveClassroomId && schoolId) {
                const selectedId = selectedRubric.id ?? null;

                if (selectedId) {
                    const isSameAsCurrent = selectedId === initialRubricIdRef.current;
                    if (isSameAsCurrent && contentDirty) {
                        await updateRubric(selectedId, {
                            name: selectedRubric.name,
                            category_weights: selectedRubric.category_weights,
                            min_passing: selectedRubric.min_passing,
                            grading_type: selectedRubric.grading_type,
                            active_grade_scheme_id: gradeSchemeId,
                        });
                    } else if (!isSameAsCurrent) {
                        rubricIdToLink = selectedId;
                    }
                } else {
                    await createRubric(
                        schoolId,
                        selectedRubric.name,
                        selectedRubric.category_weights,
                        selectedRubric.min_passing,
                        selectedRubric.grading_type,
                        gradeSchemeId,
                        effectiveClassroomId
                    );
                }
            } else if (!selectedRubric && schoolDefaultRubricId) {
                rubricIdToLink = schoolDefaultRubricId;
            }

            const classroomId = effectiveClassroomId;
            if (classroomId) {
                const patchPayload: {
                    name: string;
                    default_grade_scheme_id?: string | null;
                    rubric_id?: string | null;
                } = { name: classroomName };
                if (gradeSchemeId) {
                    patchPayload.default_grade_scheme_id = gradeSchemeId;
                }
                if (rubricIdToLink && rubricIdToLink !== initialRubricIdRef.current) {
                    patchPayload.rubric_id = rubricIdToLink;
                }
                classroomData = await editClassroomMutation.mutateAsync({ id: classroomId, payload: patchPayload });
            }

            setSaveState('completed');
            showSuccess(id ? 'Class updated successfully.' : 'Class added successfully.');
            completedTimerRef.current = setTimeout(() => {
                if(id){
                    updateClassroom(classroomData)
                }else{
                    addNewClassroom(classroomData)
                    const newId = classroomData?.id ?? classroomData?.uuid;
                    if (newId) {
                        setPendingClassroomSelection({ id: newId, name: classroomData?.name });
                    }
                }
                goBack()
            }, 800);
        }
        catch(err: any)
        {
            setSaveState('idle');
            const data = err.response?.data;
            const detail = typeof data?.detail === 'string' ? data.detail : null;
            const errorMsg = detail || data?.errors?._global || 'Unable to save class. Please try again.';
            setError(data?.errors || detail || 'Oops... an error occurred, try again later')
            showError(errorMsg);
        }
    }

    return (
        <>
            <ModalWidget
                className="add-classroom-modal"
                actionTitle={id? 'Update Class': 'Add Class'}
                actionIcon={ClassSolidSvg}
                headerIcon={ClassSvg}
                show={show}
                loading={loading}
                onHide={goBack}
                onAction={addClassroom}
                actionLoading={addClassroomMutation.isPending || editClassroomMutation.isPending}
                actionCompleted={saveState === 'completed'}
                actionDisabled={!!id && !hasChanges}
                title={id? 'Update ' + classroomName: 'Add Class'}
                description={
                    id?
                        'Update the form to make changes to ' + classroomName + ' class.':
                        'Please fill in the form to create a class. Subjects, teachers and students can be added later.'
                }
            >
                {
                    error && typeof error !== 'object' &&
                    <FormError className="mb-2">
                        {isUpgradeError(error as string)
                            ? <UpgradeErrorMessage message={error as string} />
                            : error}
                    </FormError>
                }

                <InputField label='Class Name' placeholder='Add Class Name' errorMessage={(error as any)?.name}
                            initialValue={classroomName}
                            onValueChange={(value: string)=>setClassroomName(value)}/>

                <GradingDefaultsPicker
                    variant="classroom"
                    classroomId={id}
                    selectedGradingScheme={selectedGradingScheme}
                    selectedRubric={selectedRubric}
                    onSchemeChange={(scheme) => {
                        setSelectedGradingScheme(scheme);
                        setContentDirty(true);
                    }}
                    onRubricChange={(rubric) => {
                        setSelectedRubric(rubric);
                        setContentDirty(true);
                    }}
                    onSchemeAdded={handleGradingSchemeAdded}
                    onRubricAdded={handleRubricAdded}
                    initialSchemeId={initialSchemeId}
                    initialRubricId={initialRubricId}
                    initialSchemeTemplateKey={initialSchemeTemplateKey}
                    initialRubricTemplateKey={initialRubricTemplateKey}
                    canEditGradingScheme={canEditGradingScheme}
                    canEditRubric={canEditRubric}
                    schoolDefaultSchemeId={schoolDefaultSchemeId}
                    schoolDefaultRubricId={schoolDefaultRubricId}
                    collapsedSummary={collapsedSummary}
                />
            </ModalWidget>
        </>
    );
}

export default ClassroomModal;
